/**
 * Grutto cart client — two routes only:
 *
 *   GET  /grutto/v2/cart
 *   POST /grutto/v2/cart/add-bundle
 *
 * Auth: session cookies + same-site Origin check on the server. No X-WP-Nonce / CSRF token.
 */

import axios from 'axios';
import flatstore from 'flatstore';
import { currentLang } from './functions';

const CART_TTL_MS = 30000;
const DEFAULT_ADD_DEBOUNCE_MS = 300;
const LOCAL_QTY_KEY = 'farmshop_local_qty';

let latestRequestId = 0;
let inFlightPromise = null;

/**
 * Coalesce rapid +/- clicks:
 * - debounce before send
 * - never abort an in-flight POST (browser abort does NOT stop PHP; next call then
 *   waits ~seconds on session/FPM while Server-Timing still looks "fast")
 * - while one POST runs, keep only the latest payload and flush it after
 */
let addDebounceTimer = null;
let addInFlight = null;
let addQueued = null; // { productId, bundleItems, resolve, reject } | null

const shopBase = () => `/${currentLang()}/shop/wp-json/grutto/v2`;
const cartUrl = () => `${shopBase()}/cart`;
const addToCartUrl = () => `${shopBase()}/cart/add-bundle`;

/**
 * Optimistic farmshop qty map (replaces window.product_*).
 * @param {number|string} bundledItemId
 * @param {number|string} quantity
 */
export function setFarmshopLocalQty(bundledItemId, quantity) {
  const map = { ...(flatstore.get(LOCAL_QTY_KEY) || {}) };
  map[String(bundledItemId)] = Math.max(0, parseInt(quantity, 10) || 0);
  flatstore.set(LOCAL_QTY_KEY, map);
}

/**
 * @param {number|string} bundledItemId
 * @returns {number}
 */
export function getFarmshopLocalQty(bundledItemId) {
  const map = flatstore.get(LOCAL_QTY_KEY) || {};
  return parseInt(map[String(bundledItemId)], 10) || 0;
}

/**
 * Parse cart payload into farmshop UI state.
 */
export function parseFarmshopCartItems(cartPayload) {
  const cartItems = [];
  let otherPackages = 0;

  if (!cartPayload) {
    return { cartItems, otherPackages };
  }

  if (
    cartPayload.status === 'success' ||
    cartPayload.success ||
    cartPayload.farmshop_items ||
    cartPayload.other_packages != null
  ) {
    otherPackages = parseInt(cartPayload.other_packages, 10) || 0;

    const farmshopLines = Array.isArray(cartPayload.farmshop_items)
      ? cartPayload.farmshop_items
      : [];

    farmshopLines.forEach((item) => {
      if (item?.bundled_item_id == null) return;
      cartItems.push({
        bundle_id: item.bundled_item_id,
        quantity: item.quantity,
        product_id: item.product_id,
        is_gift: item.is_gift === true || item.is_gift === 'yes',
        partial_gift_amount: item.partial_gift_amount != null
          ? parseInt(item.partial_gift_amount, 10) || 0
          : 0,
      });
    });

    return { cartItems, otherPackages };
  }

  // Legacy Store API fallback
  if (!cartPayload?.items?.length) {
    return { cartItems, otherPackages };
  }

  cartPayload.items.forEach((item) => {
    const bundles = item.extensions?.bundles;
    if (bundles?.bundled_item_data) {
      cartItems.push({
        bundle_id: bundles.bundled_item_data.bundled_item_id,
        quantity: item.quantity,
        product_id: bundles.bundled_item_data.bundle_id,
      });
    } else if (bundles && Object.keys(bundles).length === 0) {
      otherPackages++;
    }
  });

  return { cartItems, otherPackages };
}

export function markCartMutated() {
  latestRequestId++;
  flatstore.set('woocommerce_cart_stale', true);
}

export function applyFarmshopCartPayload(payload) {
  if (!payload) return payload;

  flatstore.set('woocommerce_cart', payload);
  flatstore.set('woocommerce_cart_stale', false);
  flatstore.set('woocommerce_cart_fetched_at', Date.now());

  const count = payload.count != null ? parseInt(payload.count, 10) || 0 : 0;
  const farmshopEmpty =
    !Array.isArray(payload.farmshop_items) || payload.farmshop_items.length === 0;

  // After checkout Woo may return an empty cart — drop optimistic local qtys so UI matches.
  if (count === 0 && farmshopEmpty) {
    flatstore.set(LOCAL_QTY_KEY, {});
  }

  if (payload.count != null) {
    flatstore.set('basket_count', {
      status: 'success',
      count: payload.count,
      unique_id: payload.customer_id ? `ATC_${payload.customer_id}` : undefined,
      date: payload.date,
    });
  }

  return payload;
}

/**
 * @param {{ force?: boolean, silent?: boolean }} options
 * @returns {Promise<object>}
 */
export function getWooCommerceCart(options = {}) {
  const { force = false, silent = false } = options;

  // Always share an in-flight GET (Basket + Farmshop mount together).
  // `force` only bypasses the TTL cache, not deduplication.
  if (inFlightPromise) {
    return inFlightPromise;
  }

  if (!force) {
    const cached = flatstore.get('woocommerce_cart');
    const fetchedAt = flatstore.get('woocommerce_cart_fetched_at') || 0;
    const isStale = flatstore.get('woocommerce_cart_stale');

    if (cached && !isStale && Date.now() - fetchedAt < CART_TTL_MS) {
      return Promise.resolve(cached);
    }
  }

  const requestId = ++latestRequestId;

  if (!silent) {
    flatstore.set('loading', true);
  }

  inFlightPromise = axios
    .get(cartUrl(), { withCredentials: true })
    .then((response) => {
      if (requestId !== latestRequestId) {
        return flatstore.get('woocommerce_cart') ?? response.data;
      }

      return applyFarmshopCartPayload(response.data);
    })
    .catch((err) => {
      // The cart is a secondary, session-dependent resource: a guest or crawler
      // with no cart legitimately gets a 404/401 here. Do NOT write the shared
      // global `err_data` — product/detail pages gate their <NotFound/> render on
      // that flag, so a cart error must never make a valid page render as a 404
      // (a false soft-404, especially on cold prerenders keyed by a novel query
      // string like `?test`). The rejection is still propagated; every caller
      // already handles or ignores it.
      throw err;
    })
    .finally(() => {
      // Always clear — markCartMutated() may bump latestRequestId while in flight.
      if (inFlightPromise) {
        inFlightPromise = null;
      }
      if (!silent && requestId === latestRequestId) {
        flatstore.set('loading', false);
      }
    });

  return inFlightPromise;
}

/**
 * Add / replace farmshop bundle configuration.
 *
 * Debounces rapid qty clicks and coalesces to one in-flight POST (last write wins
 * without aborting PHP).
 *
 * @param {number|string} productId Parent bundle product ID
 * @param {Record<string, { product_id: number, quantity: number }>} bundleItems
 * @param {{ debounceMs?: number }} options Use debounceMs: 0 for one-shot actions (e.g. bundle popup).
 * @returns {Promise<object>}
 */
export function addFarmshopBundleToCart(productId, bundleItems, options = {}) {
  const debounceMs =
    options.debounceMs != null ? options.debounceMs : DEFAULT_ADD_DEBOUNCE_MS;

  markCartMutated();

  return new Promise((resolve, reject) => {
    const enqueue = () => {
      // Replace any queued payload so only the latest qty map is sent next.
      if (addQueued) {
        const prev = addQueued;
        addQueued = null;
        const err = new Error('Superseded');
        err.name = 'AbortError';
        prev.reject(err);
      }

      addQueued = {
        productId: Number(productId),
        bundleItems,
        resolve,
        reject,
      };

      flushAddQueue();
    };

    if (addDebounceTimer) {
      clearTimeout(addDebounceTimer);
      addDebounceTimer = null;
    }

    if (debounceMs <= 0) {
      enqueue();
    } else {
      addDebounceTimer = setTimeout(() => {
        addDebounceTimer = null;
        enqueue();
      }, debounceMs);
    }
  });
}

async function flushAddQueue() {
  if (addInFlight || !addQueued) {
    return;
  }

  const job = addQueued;
  addQueued = null;

  addInFlight = (async () => {
    try {
      const response = await fetch(addToCartUrl(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          product_id: job.productId,
          bundle_items: job.bundleItems,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false || data.code) {
        const nested = data?.data?.message;
        const message =
          (typeof nested === 'string' && nested) ||
          (Array.isArray(nested) && nested.join(' ')) ||
          data?.message ||
          `Could not update cart (${response.status})`;

        const err = new Error(message);
        err.response = data;
        err.status = response.status;
        throw err;
      }

      applyFarmshopCartPayload(data);
      job.resolve(data);
    } catch (err) {
      job.reject(err);
    } finally {
      addInFlight = null;
      if (addQueued) {
        flushAddQueue();
      }
    }
  })();

  await addInFlight;
}
